import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../utils/firebase';
import {
  HomeIcon,
  FolderIcon,
  TrophyIcon,
  ChartBarIcon,
  ListBulletIcon,
  ChartPieIcon,
  ArrowUpTrayIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  HomeModernIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import BackToTop from '../common/BackToTop';
import logo from '../../assets/logo.png';

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = [
    { key: '/', icon: HomeIcon, label: 'Dashboard' },
    { key: '/accounts', icon: FolderIcon, label: 'Accounts' },
    { key: '/real-estate', icon: HomeModernIcon, label: 'Real Estate' },
    { key: '/funds', icon: TrophyIcon, label: 'Funds' },
    { key: '/school-fees', icon: AcademicCapIcon, label: 'School Fees' },
    { key: '/allocations', icon: ChartPieIcon, label: 'Allocations' },
    { key: '/transactions', icon: ListBulletIcon, label: 'Transactions' },
    { key: '/reports', icon: ChartBarIcon, label: 'Reports' },
    { key: '/fund-prices', icon: BanknotesIcon, label: 'Fund Prices' },
    { key: '/import-export', icon: ArrowUpTrayIcon, label: 'Import/Export' },
    { key: '/settings', icon: Cog6ToothIcon, label: 'Settings' },
  ];

  const handleMenuClick = (key) => {
    navigate(key);
  };

  const handleSignOut = () => {
    signOut(auth);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-screen z-50 transition-all duration-200 flex flex-col ${
          collapsed ? 'w-20' : 'w-64'
        }`}
        style={{
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRight: '1px solid #cbd5e1',
          boxShadow: '2px 0 10px rgba(30, 58, 95, 0.08)',
        }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo/Header */}
          <div
            className="flex items-center justify-center py-6 px-6 flex-shrink-0"
            style={{
              borderBottom: '1px solid #cbd5e1',
              minHeight: '90px',
            }}
          >
            {!collapsed ? (
              <button
                onClick={() => navigate('/')}
                className="flex flex-row items-center gap-3 transition-opacity hover:opacity-80 cursor-pointer bg-transparent border-none"
              >
                <img
                  src={logo}
                  alt="SteadyShip Logo"
                  className="h-10 w-auto"
                />
                <span className="text-xl font-semibold tracking-tight" style={{ color: '#012E58', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                  SteadyShip
                </span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/')}
                className="transition-opacity hover:opacity-80 cursor-pointer border-none bg-transparent p-0"
              >
                <img
                  src={logo}
                  alt="SteadyShip"
                  className="h-8 w-auto"
                />
              </button>
            )}
          </div>

          {/* Menu Items */}
          <div className="flex-1 flex flex-col gap-1 px-3 py-6">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.key
                || (item.key === '/real-estate' && (location.pathname.startsWith('/properties') || location.pathname.startsWith('/mortgages')))
                || (item.key === '/school-fees' && location.pathname.startsWith('/school-fees/'))
                || (item.key === '/funds' && location.pathname.startsWith('/funds/'));

              return (
                <div key={item.key}>
                  <button
                    onClick={() => handleMenuClick(item.key)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                      ${collapsed ? 'justify-center' : 'justify-start'}
                      ${isActive ? 'text-[#1e3a5f] font-semibold shadow-sm' : 'text-gray-700 font-medium'}
                      ${!isActive && 'hover:bg-slate-100'}
                    `}
                    style={isActive ? { backgroundColor: '#dce8f3' } : {}}
                    title={item.label}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-[#2d5a8c]' : 'text-gray-500'}`} />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </button>
                </div>
              );
            })}
          </div>

          {/* User Section */}
          <div className="px-3 py-3 flex-shrink-0" style={{ borderTop: '1px solid #cbd5e1' }}>
            <button
              onClick={handleSignOut}
              className={`w-full flex items-center ${collapsed ? 'justify-center py-2.5' : 'justify-center gap-2 py-2.5 px-3'} text-gray-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors`}
              title={`Log out (${currentUser?.email})`}
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && (
                <span className="text-xs font-medium text-gray-900 truncate">{currentUser?.email}</span>
              )}
            </button>
          </div>

          {/* Collapse Toggle */}
          <div className="px-3 py-3 flex-shrink-0" style={{ borderTop: '1px solid #cbd5e1' }}>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-full flex items-center justify-center py-2.5 text-[#4a5f7a] bg-slate-100 hover:bg-slate-200 rounded-xl transition-all duration-200 font-medium text-sm"
            >
              {collapsed ? <ChevronRightIcon className="w-5 h-5" /> : <ChevronLeftIcon className="w-5 h-5" />}
              {!collapsed && <span className="ml-2">Collapse</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 h-screen flex flex-col transition-all duration-200 overflow-hidden ${
          collapsed ? 'ml-20' : 'ml-64'
        }`}
        style={{
          background: '#f9fafb',
        }}
      >
        {/* Page Content */}
        <div className="p-8 flex-1 flex flex-col overflow-auto min-w-0">
          {children}
        </div>
      </div>

      {/* Back to Top Button */}
      <BackToTop />
    </div>
  );
};

export default MainLayout;
